import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, CreditCard, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { buildAdminApi } from "../lib/admin";
import { toast } from "sonner";

/**
 * TrialBanner — shows the café's trial/subscription state and CTA to upgrade or manage billing.
 * Renders in the AdminLayout so it appears on every admin subpage.
 */
const TrialBanner = () => {
    const { slug } = useParams();
    const adminApi = useMemo(() => buildAdminApi(slug), [slug]);
    const [sub, setSub] = useState(null);
    const [checkoutBusy, setCheckoutBusy] = useState(null);
    const [portalBusy, setPortalBusy] = useState(false);

    useEffect(() => {
        adminApi
            .get("/subscription")
            .then((r) => setSub(r.data))
            .catch(() => {
                /* silent — probably first load or auth glitch */
            });
    }, [slug, adminApi]);

    if (!sub) return null;

    const startCheckout = async (tier) => {
        setCheckoutBusy(tier);
        try {
            const r = await adminApi.post("/subscription/checkout", {
                tier,
                origin_url: window.location.origin,
            });
            if (r.data?.checkout_url) window.location.assign(r.data.checkout_url);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't start checkout");
        } finally {
            setCheckoutBusy(null);
        }
    };

    const openPortal = async () => {
        setPortalBusy(true);
        try {
            const r = await adminApi.post("/subscription/portal", {
                origin_url: window.location.origin,
            });
            if (r.data?.portal_url) window.location.assign(r.data.portal_url);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't open billing");
        } finally {
            setPortalBusy(false);
        }
    };

    const status = sub.subscription_status;
    const daysLeft = sub.trial_days_left ?? 0;
    const graceLeft = sub.grace_days_left ?? 0;
    const effective = sub.effective_state || status;
    const warnLevel = sub.warn_level || "none";

    // Active paid subscription — small green pill
    if (status === "active") {
        return (
            <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 rounded-2xl bg-[#1B995E]/10 border border-[#1B995E]/30 px-4 py-3 flex items-center justify-between flex-wrap gap-3"
                data-testid="trial-banner-active"
            >
                <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-[#1B995E]" strokeWidth={2.5} />
                    <span className="font-display font-bold text-[#0f6b41]">
                        {`Coffeein7 ${sub.subscription_tier === "pro" ? "Pro" : "Starter"} · Active`}
                    </span>
                </div>
                <button
                    data-testid="trial-manage-billing"
                    onClick={openPortal}
                    disabled={portalBusy}
                    className="text-xs font-display font-bold text-[#0f6b41] hover:underline flex items-center gap-1"
                >
                    {portalBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CreditCard className="h-3 w-3" strokeWidth={2.5} />}
                    Manage billing
                </button>
            </motion.div>
        );
    }

    // Hard-locked (unpaid, past grace period). Full-width paywall — nothing
    // else on the admin page should feel usable until the owner pays.
    if (sub.is_locked || effective === "suspended") {
        return (
            <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 rounded-2xl bg-[#2C1A12] text-white p-6"
                data-testid="trial-banner-suspended"
            >
                <div className="flex items-center gap-2 text-[#FF4F18] text-[10px] uppercase tracking-[0.3em] font-bold mb-2">
                    <AlertTriangle className="h-3 w-3" strokeWidth={2.5} />
                    Café paused
                </div>
                <div className="font-display font-black text-2xl leading-tight">
                    Your café is offline. Customers scanning your QR see a temporary
                    "unavailable" screen.
                </div>
                <div className="mt-2 text-white/70 text-sm">
                    Choose a plan to bring your café back online instantly.
                </div>
                <div className="flex flex-wrap gap-2 mt-5">
                    <button
                        data-testid="trial-upgrade-starter"
                        onClick={() => startCheckout("starter")}
                        disabled={checkoutBusy !== null}
                        className="rounded-full bg-white text-[#2C1A12] px-5 py-3 text-sm font-display font-black active:scale-95 flex items-center gap-2"
                    >
                        {checkoutBusy === "starter" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" strokeWidth={2.5} />}
                        Starter · $49/mo
                    </button>
                    <button
                        data-testid="trial-upgrade-pro"
                        onClick={() => startCheckout("pro")}
                        disabled={checkoutBusy !== null}
                        className="rounded-full bg-[#FF4F18] text-white px-5 py-3 text-sm font-display font-black active:scale-95 flex items-center gap-2 shadow-[0_12px_32px_rgba(255,79,24,0.4)]"
                    >
                        {checkoutBusy === "pro" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" strokeWidth={2.5} />}
                        Pro · $79/mo
                    </button>
                </div>
            </motion.div>
        );
    }

    // Grace period (trial ended OR payment failed) — red danger banner.
    if (effective === "trial_grace" || effective === "past_due_grace") {
        const isTrialGrace = effective === "trial_grace";
        return (
            <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 rounded-2xl bg-[#FF4F18] text-white p-5"
                data-testid="trial-banner-grace"
            >
                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" strokeWidth={2.5} />
                        <div>
                            <div className="font-display font-black text-lg leading-tight">
                                {isTrialGrace
                                    ? "Your free trial ended"
                                    : "Payment failed — card needs updating"}
                            </div>
                            <div className="text-white/85 text-sm mt-1">
                                {graceLeft > 0
                                    ? `Add a card in the next ${graceLeft} day${graceLeft === 1 ? "" : "s"} or your café goes offline.`
                                    : "Your café will pause imminently. Update your card now."}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            data-testid="trial-upgrade-starter"
                            onClick={() => startCheckout("starter")}
                            disabled={checkoutBusy !== null}
                            className="rounded-full bg-white text-[#2C1A12] px-4 py-2 text-xs font-display font-bold active:scale-95"
                        >
                            {checkoutBusy === "starter" ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                            Starter · $49
                        </button>
                        <button
                            data-testid="trial-upgrade-pro"
                            onClick={() => startCheckout("pro")}
                            disabled={checkoutBusy !== null}
                            className="rounded-full bg-[#2C1A12] text-white px-4 py-2 text-xs font-display font-bold active:scale-95"
                        >
                            {checkoutBusy === "pro" ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                            Pro · $79
                        </button>
                    </div>
                </div>
            </motion.div>
        );
    }

    // Trial (still active) — orange for last 7 days, amber 8-30, ghost otherwise
    const trialDanger = warnLevel === "warning";
    const trialUrgent = daysLeft <= 30 && daysLeft > 7;

    const banner = trialDanger
        ? {
              icon: AlertTriangle,
              tone: "bg-[#FF4F18]/10 border-[#FF4F18]/40",
              text: "text-[#c73810]",
              label: `${daysLeft} day${daysLeft === 1 ? "" : "s"} left in your free trial`,
          }
        : trialUrgent
        ? {
              icon: Sparkles,
              tone: "bg-[#FFB020]/10 border-[#FFB020]/40",
              text: "text-[#96660E]",
              label: `${daysLeft} days left in your free trial`,
          }
        : {
              icon: Sparkles,
              tone: "bg-[#FF4F18]/5 border-[#FF4F18]/25",
              text: "text-[#2C1A12]",
              label:
                  status === "trial"
                      ? `You're on your free trial · ${daysLeft} days remaining`
                      : "Choose a plan to keep your café live",
          };

    const Icon = banner.icon;

    return (
        <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 rounded-2xl border-2 ${banner.tone} px-5 py-4 flex items-center justify-between flex-wrap gap-4`}
            data-testid="trial-banner"
        >
            <div className="flex items-center gap-3">
                <Icon className={`h-5 w-5 shrink-0 ${banner.text}`} strokeWidth={2.5} />
                <div>
                    <div className={`font-display font-black ${banner.text}`}>{banner.label}</div>
                    <div className="text-xs text-[#8C7A72] mt-0.5">
                        Starter $49/mo · Pro $79/mo · Cancel anytime · 14-day money-back
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                <button
                    data-testid="trial-upgrade-starter"
                    onClick={() => startCheckout("starter")}
                    disabled={checkoutBusy !== null}
                    className="rounded-full bg-white border-2 border-[#2C1A12] text-[#2C1A12] px-4 py-2 text-xs font-display font-bold active:scale-95 hover:bg-[#F5F1E8] transition-colors flex items-center gap-1"
                >
                    {checkoutBusy === "starter" ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                    ) : null}
                    Starter · $49
                </button>
                <button
                    data-testid="trial-upgrade-pro"
                    onClick={() => startCheckout("pro")}
                    disabled={checkoutBusy !== null}
                    className="rounded-full bg-[#FF4F18] text-white px-4 py-2 text-xs font-display font-bold active:scale-95 flex items-center gap-1 shadow-[0_8px_20px_rgba(255,79,24,0.35)]"
                >
                    {checkoutBusy === "pro" ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                    ) : null}
                    Pro · $79
                </button>
            </div>
        </motion.div>
    );
};

export default TrialBanner;
